// Loop Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  Loop as LoopIcon,
  Repeat as RepeatIcon,
  FormatListNumbered as ForEachIcon
} from '@mui/icons-material';

interface LoopNodeData {
  label: string;
  description?: string;
  loopType?: 'forEach' | 'while' | 'repeat';
  status?: 'idle' | 'running' | 'completed' | 'error';
  currentIteration?: number;
  totalIterations?: number;
  collection?: string;
}

const LoopNode: React.FC<NodeProps<LoopNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getLoopIcon = () => {
    switch (data.loopType) {
      case 'forEach':
        return <ForEachIcon />;
      case 'repeat':
        return <RepeatIcon />;
      default:
        return <LoopIcon />;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return '#E3F2FD';
      case 'completed':
        return '#E8F5E9';
      case 'error':
        return '#FFEBEE';
      default:
        return '#FFF3E0';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'running':
        return '#2196F3';
      case 'completed':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      default:
        return '#FF9800';
    }
  };

  const formatLoopType = (type?: string) => {
    if (!type) return 'Loop';
    return type.replace(/([A-Z])/g, ' $1').trim();
  };

  const getProgress = () => {
    if (data.currentIteration !== undefined && data.totalIterations !== undefined) {
      return Math.round((data.currentIteration / data.totalIterations) * 100);
    }
    return 0;
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />
      
      {/* Circular Loop Container */}
      <Box
        sx={{
          position: 'relative',
          width: 120,
          height: 120,
          borderRadius: '50%',
          backgroundColor: getStatusColor(),
          border: `3px solid ${getBorderColor()}`,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 6 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            boxShadow: 8,
            transform: 'scale(1.05)'
          }
        }}
      >
        {/* Rotating border for running state */}
        {data.status === 'running' && (
          <Box
            sx={{
              position: 'absolute',
              top: -5,
              left: -5,
              right: -5,
              bottom: -5,
              borderRadius: '50%',
              background: `conic-gradient(from 0deg, ${getBorderColor()}, transparent, ${getBorderColor()})`,
              animation: 'rotate 2s linear infinite',
              zIndex: 0
            }}
          />
        )}

        {/* Progress ring */}
        {data.status === 'running' && data.currentIteration !== undefined && data.totalIterations !== undefined && (
          <Box
            sx={{
              position: 'absolute',
              top: -3,
              left: -3,
              right: -3,
              bottom: -3,
              borderRadius: '50%',
              background: `conic-gradient(${getBorderColor()} ${getProgress() * 3.6}deg, transparent 0deg)`,
              zIndex: 1
            }}
          />
        )}

        {/* Content */}
        <Box
          sx={{
            textAlign: 'center',
            zIndex: 2,
            backgroundColor: getStatusColor(),
            borderRadius: '50%',
            width: '90%',
            height: '90%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              color: getBorderColor(),
              mb: 1,
              animation: data.status === 'running' ? 'spin 2s linear infinite' : 'none'
            }}
          >
            {getLoopIcon()}
          </Box>

          {/* Label */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              lineHeight: 1,
              textAlign: 'center',
              mb: 0.5
            }}
          >
            {data.label}
          </Typography>

          {/* Loop type */}
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              color: 'text.secondary',
              textAlign: 'center'
            }}
          >
            {formatLoopType(data.loopType)}
          </Typography>

          {/* Iteration count */}
          {data.currentIteration !== undefined && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                color: getBorderColor(),
                fontWeight: 600,
                mt: 0.5
              }}
            >
              {data.totalIterations 
                ? `${data.currentIteration}/${data.totalIterations}`
                : `${data.currentIteration}`
              }
            </Typography>
          )}
        </Box>
      </Box>

      {/* Collection info */}
      {data.collection && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Chip
            label={`${data.collection}`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              backgroundColor: `${getBorderColor()}20`,
              color: getBorderColor()
            }}
          />
        </Box>
      )}

      {/* Output Handles */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="body"
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%',
          left: '30%'
        }}
      />
      
      <Handle
        type="source"
        position={Position.Bottom}
        id="complete"
        style={{
          background: '#4CAF50',
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%',
          left: '70%'
        }}
      />

      {/* Labels for outputs */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -25,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          px: 1
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: getBorderColor(),
            fontWeight: 600
          }}
        >
          LOOP
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: '#4CAF50',
            fontWeight: 600
          }}
        >
          DONE
        </Typography>
      </Box>

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(LoopNode);